/**
 * devisPrestationPdfGenerator.ts
 *
 * Génère le PDF du devis de prestation côté navigateur, sur le même patron que
 * src/components/Factures/invoicePdfGenerator.ts : un gabarit HTML rendu dans un iframe caché, capturé en
 * image, puis assemblé en PDF avec jsPDF. Structure calquée sur la facture (établissement + destinataire
 * en blocs complets, pied de page légal) — demande d'Emeni (2026-09-03), le devis était jusqu'ici réduit
 * au nom de l'établissement et à celui du client, sans aucune coordonnée.
 */
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { moduleApi } from '../../lib/api';

const fmt = (val: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(val) ? val : 0);

export interface DevisPrestationLine {
    label: string;
    quantity: number;
    unit_price_ttc: number;
    tax_rate: number;
    line_ttc: number;
    discount_amount: number;
    // Ligne de remise/offre négative générée par computeDevisTotals (offre sur quantité ou réduction en %
    // par ligne) — affichée sans prix unitaire ni note de quantité, juste son libellé déjà explicite et son
    // montant négatif : jamais un total simplement réduit sans que la remise soit lisible sur le devis.
    is_discount_line?: boolean;
    // Devis annuel par passages (FormulaPassagesPlanner.tsx) — absents pour un devis classique.
    passage_number?: number | null;
    passage_label?: string | null;
    // Réduction par lots (tarifs professionnels) : prix du catalogue avant réduction, et pourcentage appliqué.
    list_unit_price_ttc?: number;
    volume_discount_pct?: number;
}

// Coordonnées de l'établissement (mêmes champs que billing_settings, cf. invoicePdfGenerator.ts) — un
// sous-ensemble suffit ici, tous facultatifs pour ne jamais bloquer la génération si un champ manque.
const echapperHtmlDevis = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface DevisEtablissementInfo {
    address?: string; postal_code?: string; city?: string; phone?: string;
    siret?: string; tva?: string; ape?: string; capital?: string; siege_social?: string;
    rcs_ville?: string; rcs_numero?: string;
    // Médiateur de la consommation de l'établissement (réglage unique, repris aussi dans les CGV et le contrat) : imprimé pour un client particulier.
    cgv_mediator?: string;
    // 'franchise_293b' : micro-entreprise en franchise en base de TVA (même réglage que la facture).
    tva_regime?: 'normal' | 'franchise_293b' | string;
}

// Coordonnées du destinataire — reprises du contact CRM (particulier ou professionnel) quand disponibles ;
// sans elles on retombe simplement sur le nom seul, comme avant.
export interface DevisClientInfo {
    address?: string; postal_code?: string; city?: string; email?: string; phone?: string;
    // Particulier ou professionnel : décide des mentions légales imprimées sur le devis. Le SIRET et la TVA
    // intracommunautaire du client ne sont repris que pour un professionnel.
    type?: 'PRIVÉ' | 'PROFESSIONNEL';
    siret?: string; vat_number?: string;
}

// Durée de validité écrite sur le devis : réglée dans Planning > Paramètres > Délais (30 jours tant qu'elle n'est pas réglée).
let validiteDevisJours = 30;
// Signer le devis engage-t-il le client ? (Planning > Paramètres > Délais) : oui tant que l'établissement n'a pas dit le contraire.
let devisEngageClient = true;
let validiteChargee = false;
export async function chargerValiditeDevis(): Promise<number> {
    if (validiteChargee) return validiteDevisJours;
    try {
        const r: any = await moduleApi.getPrestationDelais();
        const n = Number(r?.devis_validite_jours);
        if (Number.isFinite(n) && n > 0) validiteDevisJours = Math.round(n);
        devisEngageClient = r?.devis_engage_client !== false;
        validiteChargee = true;
    } catch { /* hors connexion ou accès refusé : on garde 30 */ }
    return validiteDevisJours;
}
/** À appeler après un changement du réglage, pour que le prochain devis le relise. */
export function oublierValiditeDevis() { validiteChargee = false; }

export function buildDevisPrestationHtml(params: {
    companyName: string;
    logoUrl?: string | null;
    accentColor?: string;
    etablissement?: DevisEtablissementInfo | null;
    clientLabel: string;
    client?: DevisClientInfo | null;
    devisNumber?: string | null;
    // Code du commercial qui a fait le devis (module Commerciaux) : le client le retrouve par ce code, pas par son nom.
    codeVendeur?: string | null;
    lines: DevisPrestationLine[];
    totalHt: number;
    totalTva: number;
    totalTtc: number;
    // Devis annuel basé sur une formule (FormulaPassagesPlanner.tsx) — ajoute une note légale informant le
    // client que toute prestation hors de ce planning sera facturée en supplément (demande d'Emeni, 2026-09-03).
    formuleAnnuelle?: boolean;
    // Résumé de la formule (nombre de passages inclus, seuil minimum par passage) — affiché en encadré sous
    // le destinataire, demande d'Emeni (2026-09-03) : le client doit voir ces règles noir sur blanc.
    formuleResume?: { nom: string; occurrencesTotal: number; seuils: { category_name: string; minimum_quantity: number }[]; seuilsMode: 'any' | 'all'; seuilMontant?: number } | null;
}): string {
    const { companyName, lines, totalHt, totalTva, totalTtc, clientLabel, devisNumber, formuleAnnuelle, formuleResume, codeVendeur } = params;
    const accent = params.accentColor || '#2f9e9e';
    const logoUrl = params.logoUrl ? resolveLogoUrl(params.logoUrl) : null;
    const date = new Date().toLocaleDateString('fr-FR');
    const settings = params.etablissement || {};
    const client = params.client || {};

    const clientHasAddress = client.address || client.postal_code || client.city;

    const legalParts1: string[] = [];
    if (settings.rcs_ville && settings.rcs_numero) legalParts1.push(`RCS ${settings.rcs_ville} ${settings.rcs_numero}`);
    if (settings.siret) legalParts1.push(`• SIRET ${settings.siret}`);
    if (settings.tva) legalParts1.push(`• TVA ${settings.tva}`);
    if (settings.tva_regime === 'franchise_293b') legalParts1.push('• TVA non applicable, art. 293 B du CGI');
    if (settings.ape) legalParts1.push(`• Code APE ${settings.ape}`);

    const legalParts2: string[] = [];
    if (settings.capital) legalParts2.push(`Capital social : ${settings.capital}`);
    if (settings.siege_social) legalParts2.push(`• ${settings.siege_social}`);
    else if (settings.address || settings.city) {
        const addr = [settings.address, settings.postal_code, settings.city].filter(Boolean).join(' ');
        if (addr) legalParts2.push(`• ${addr}`);
    }

    // Devis annuel par passages : chaque changement de passage_number insère une ligne d'en-tête « Passage
    // N — période » dans le tableau, pour que le client voie clairement à quoi correspond chaque ligne.
    let dernierPassage: number | null | undefined = undefined;
    const rows = lines.map((l) => {
        const enTete = l.passage_number !== dernierPassage
            ? (dernierPassage = l.passage_number, l.passage_number != null ? `
        <tr><td colspan="5" style="padding:14px 0 4px 0;font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:0.08em;color:${accent};border-top:1px solid #f1f5f9;">Passage ${l.passage_number}${l.passage_label ? ` — ${l.passage_label}` : ''}</td></tr>` : '')
            : '';
        return enTete + (l.is_discount_line ? `
        <tr>
            <td style="padding:6px 0;" colspan="3">
                <p style="font-weight:700;color:#64748b;font-size:12px;margin:0;font-style:italic;">${l.label}</p>
            </td>
            <td style="padding:6px 0 6px 16px;text-align:right;font-weight:600;color:#dc2626;font-size:12px;">${fmt(l.line_ttc / (1 + (l.tax_rate || 0) / 100))}</td>
            <td style="padding:6px 0 6px 16px;text-align:right;font-weight:700;color:#dc2626;font-size:13px;">${fmt(l.line_ttc)}</td>
        </tr>` : (() => {
            // Prix unitaire et total AVANT/APRÈS avec le pourcentage de réduction formule, ancien prix barré
            // au-dessus du nouveau — compact, demande d'Emeni (2026-09-03).
            // Prix du catalogue avant toute réduction (lots + formule), pour que le devis montre tout ce que le client économise.
            const prixListe = l.list_unit_price_ttc ?? l.unit_price_ttc;
            const totalAvant = prixListe * l.quantity;
            const puAvant = Math.max(prixListe, l.unit_price_ttc);
            const pct = totalAvant > 0 && totalAvant - l.line_ttc > 0.005 ? Math.round(((totalAvant - l.line_ttc) / totalAvant) * 100) : 0;
            const unitApres = l.quantity > 0 ? l.line_ttc / l.quantity : l.unit_price_ttc;
            const volPct = Number(l.volume_discount_pct) || 0;
            const formPct = l.discount_amount > 0 && l.unit_price_ttc * l.quantity > 0 ? Math.round((l.discount_amount / (l.unit_price_ttc * l.quantity)) * 100) : 0;
            return `
        <tr>
            <td style="padding:16px 0;">
                <p style="font-weight:700;color:#1e293b;font-size:16px;margin:0 0 2px 0;">${l.label}</p>
                <p style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;letter-spacing:0.06em;margin:0;">${l.quantity} unité${l.quantity > 1 ? 's' : ''}${volPct > 0 ? ` — tarif volume -${volPct}%` : ''}${(l as any).formula_free_quantity > 0 ? ((l as any).formula_free_quantity >= l.quantity ? ' — offert (formule)' : ` — dont ${(l as any).formula_free_quantity} offertes (formule)`) : formPct > 0 ? ` — formule -${formPct}%` : ''}</p>
            </td>
            <td style="padding:16px 0;text-align:right;font-size:13px;">
                ${pct > 0 ? `<p style="margin:0;color:#ef4444;text-decoration:line-through;font-size:10px;">${fmt(puAvant / (1 + (l.tax_rate || 0) / 100))}</p>` : ''}
                <p style="margin:0;color:#475569;${pct > 0 ? 'font-weight:700;' : ''}">${fmt(unitApres / (1 + (l.tax_rate || 0) / 100))}</p>
            </td>
            <td style="padding:16px 0 16px 16px;text-align:right;font-size:13px;">
                ${pct > 0 ? `<p style="margin:0;color:#ef4444;text-decoration:line-through;font-size:10px;">${fmt(puAvant)}</p>` : ''}
                <p style="margin:0;color:#475569;${pct > 0 ? 'font-weight:700;' : ''}">${fmt(unitApres)}</p>
            </td>
            <td style="padding:16px 0 16px 24px;text-align:right;">
                ${pct > 0 ? `<p style="margin:0;color:#ef4444;text-decoration:line-through;font-size:10px;">${fmt(totalAvant / (1 + (l.tax_rate || 0) / 100))}</p>` : ''}
                <p style="margin:0;font-weight:700;color:#475569;font-size:14px;">${fmt(l.line_ttc / (1 + (l.tax_rate || 0) / 100))}</p>
            </td>
            <td style="padding:16px 0 16px 24px;text-align:right;">
                ${pct > 0 ? `<p style="margin:0;color:#ef4444;text-decoration:line-through;font-size:10px;">${fmt(totalAvant)}</p>` : ''}
                <p style="margin:0;font-weight:900;color:#0f172a;font-size:16px;">${fmt(l.line_ttc)}</p>
            </td>
        </tr>`;
        })());
    }).join('');

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8"/>
<title>${devisNumber || 'Devis'}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #1e293b; }
  .devis-wrap {
    background: white;
    padding: 8mm;
    width: 210mm;
    display: flex;
    flex-direction: column;
    position: relative;
  }
</style>
</head>
<body>
<div class="devis-wrap">

  <!-- HEADER -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;gap:24px;">
    <div style="display:flex;flex-direction:column;min-width:0;">
      ${logoUrl
          ? `<img src="${logoUrl}" alt="Logo" style="max-height:112px;max-width:250px;margin-bottom:8px;object-fit:contain;align-self:flex-start;" />`
          : `<div style="font-size:11px;font-weight:900;letter-spacing:0.2em;color:#94a3b8;margin-bottom:8px;">LOGO</div>`
      }
      <h1 style="font-size:20px;font-weight:700;color:#1e293b;">${companyName}</h1>
      <div style="color:#475569;font-size:11px;margin-top:4px;line-height:1.4;">
        <p>${settings.address || '-'}</p>
        <p>${[settings.postal_code, settings.city].filter(Boolean).join(' ') || '-'}</p>
      </div>
      ${settings.phone ? `<p style="margin-top:4px;font-size:11px;color:#475569;font-weight:600;">Tél : ${settings.phone}</p>` : ''}
    </div>

    <div style="text-align:right;flex-shrink:0;">
      <h2 style="font-size:32px;font-weight:900;text-transform:uppercase;color:${accent};letter-spacing:-0.035em;margin-bottom:8px;">DEVIS</h2>
      <div style="background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #f1f5f9;display:inline-block;text-align:left;min-width:190px;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="margin-bottom:8px;">
          <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Numéro de devis</p>
          <p style="font-family:monospace;font-size:15px;font-weight:700;color:#0f172a;">${devisNumber || '—'}</p>
        </div>
        <div style="padding-top:8px;border-top:1px solid rgba(226,232,240,0.6);">
          <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Date</p>
          <p style="font-weight:700;color:#0f172a;font-size:11px;">${date}</p>
        </div>
        ${codeVendeur ? `<div style="padding-top:8px;margin-top:8px;border-top:1px solid rgba(226,232,240,0.6);">
          <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Code vendeur</p>
          <p style="font-family:monospace;font-size:13px;font-weight:700;color:#0f172a;">${codeVendeur}</p>
        </div>` : ''}
      </div>
    </div>
  </div>

  <!-- DESTINATAIRE -->
  <div style="margin-bottom:24px;display:flex;justify-content:flex-end;">
    <div style="width:50%;padding:16px;border-radius:12px;border:2px solid #f8fafc;background:white;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
      <h3 style="font-size:9px;font-weight:900;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:8px;color:${accent};opacity:0.7;">Destinataire</h3>
      <p style="font-weight:700;font-size:18px;color:#0f172a;margin-bottom:2px;">${clientLabel || '-'}</p>
      <div style="color:#475569;font-size:11px;line-height:1.6;">
        ${clientHasAddress ? `
        <p>${client.address || '-'}</p>
        <p>${[client.postal_code, client.city].filter(Boolean).join(' ') || '-'}</p>` : ''}
        ${client.type === 'PROFESSIONNEL' && client.siret ? `<p style="margin-top:4px;">SIRET ${client.siret}</p>` : ''}
        ${client.type === 'PROFESSIONNEL' && client.vat_number ? `<p>TVA intracommunautaire ${client.vat_number}</p>` : ''}
        ${client.email ? `<p style="margin-top:4px;">${client.email}</p>` : ''}
        ${client.phone ? `<p>${client.phone}</p>` : ''}
      </div>
    </div>
  </div>

  ${formuleResume ? `
  <div style="margin-bottom:24px;padding:12px 16px;background:#f8fafc;border-radius:10px;border:1px solid #f1f5f9;">
    <p style="font-size:9px;font-weight:900;text-transform:uppercase;letter-spacing:0.1em;color:${accent};opacity:0.7;margin-bottom:4px;">Formule ${formuleResume.nom}</p>
    <p style="font-size:11px;color:#475569;">${formuleResume.occurrencesTotal} passage${formuleResume.occurrencesTotal > 1 ? 's' : ''} inclus sur l'année.${(formuleResume.seuilMontant || 0) > 0
        ? ` Minimum ${(formuleResume.seuilMontant || 0).toFixed(2).replace('.', ',')} € TTC pour une prestation, à atteindre à chaque passage.`
        : formuleResume.seuils.length > 0 ? ` Seuil minimum requis à chaque passage (${formuleResume.seuilsMode === 'all' ? 'tous obligatoires' : 'un seul suffit'}) : ${formuleResume.seuils.map((s) => `${s.minimum_quantity} ${s.category_name}`).join(formuleResume.seuilsMode === 'all' ? ' et ' : ' ou ')}.` : ''}</p>
  </div>` : ''}

  <!-- TABLE -->
  <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
    <thead>
      <tr style="border-bottom:2px solid #0f172a;text-align:left;">
        <th style="padding-bottom:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;">Prestation</th>
        <th style="padding-bottom:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;text-align:right;letter-spacing:0.1em;">P.U. HT</th>
        <th style="padding-bottom:8px;padding-left:16px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;text-align:right;letter-spacing:0.1em;">P.U. TTC</th>
        <th style="padding-bottom:8px;padding-left:24px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;text-align:right;letter-spacing:0.1em;">Total HT</th>
        <th style="padding-bottom:8px;padding-left:24px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;text-align:right;letter-spacing:0.1em;">Total TTC</th>
      </tr>
    </thead>
    <tbody style="border-top:1px solid #f1f5f9;">${rows}</tbody>
  </table>

  ${formuleAnnuelle ? `<p style="font-size:10px;color:#94a3b8;font-style:italic;margin-bottom:16px;">Ce devis couvre les passages planifiés sur l'année selon la formule souscrite ci-dessus. En signant ce devis, le client s'engage à atteindre le seuil minimum requis à chaque passage. Les montants indiqués sont établis sur la base des prestations planifiées et restent susceptibles d'évoluer : toute prestation demandée en cours d'année en dehors de ce planning fera l'objet d'une facturation séparée, au tarif préférentiel de la formule souscrite lorsqu'applicable.</p>` : ''}

  <!-- BOTTOM BLOCK : ancré en bas de la page où il atterrit (2026-09-05, Emeni), cf. le calcul de
       marge ajouté dans generateDevisPrestationPdf ci-dessous. -->
  <div class="pdf-anchor-bottom" style="margin-top:16px;">
    ${(() => {
        // Total avant réduction/offert (prix plein des lignes réelles, hors sous-lignes de remise négatives) —
        // affiché barré à côté du total final si différent, même compact que le prix unitaire par ligne.
        const lignesReelles = lines.filter((l) => !l.is_discount_line);
        const totalTtcAvant = lignesReelles.reduce((s, l) => s + (l.list_unit_price_ttc ?? l.unit_price_ttc) * l.quantity, 0);
        const totalHtAvant = lignesReelles.reduce((s, l) => s + ((l.list_unit_price_ttc ?? l.unit_price_ttc) * l.quantity) / (1 + l.tax_rate / 100), 0);
        const totalTvaAvant = totalTtcAvant - totalHtAvant;
        const reduit = totalTtcAvant - totalTtc > 0.005;
        const barre = (montant: number) => reduit ? `<span style="color:#ef4444;text-decoration:line-through;margin-right:6px;">${fmt(montant)}</span>` : '';
        return `
    <div class="pdf-avoid-break" style="display:flex;justify-content:flex-end;margin-bottom:16px;">
      <div style="width:320px;background:#f8fafc;padding:20px;border-radius:16px;border:1px solid #f1f5f9;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#64748b;">
          <span>Sous-total Hors Taxes :</span><span>${barre(totalHtAvant)}<span style="color:#0f172a;">${fmt(totalHt)}</span></span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#64748b;margin-top:8px;padding-top:8px;border-top:1px solid rgba(226,232,240,0.6);">
          <span>TVA :</span><span>${settings.tva_regime === 'franchise_293b' ? 'non applicable (art. 293 B du CGI)' : fmt(totalTva)}</span>
        </div>
        <div style="padding-top:16px;margin-top:4px;border-top:2px solid ${accent};display:flex;justify-content:space-between;align-items:baseline;">
          <span style="font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:0.1em;color:${accent};">Total TTC</span>
          <span>${reduit ? `<span style="font-size:13px;color:#ef4444;text-decoration:line-through;margin-right:6px;">${fmt(totalTtcAvant)}</span>` : ''}<span style="font-size:24px;font-weight:900;color:${accent};">${fmt(totalTtc)}</span></span>
        </div>
      </div>
    </div>`;
    })()}

    <!-- MENTIONS LÉGALES DU DEVIS, selon que le client est un particulier ou un professionnel -->
    <div class="pdf-avoid-break" style="margin-top:8px;padding:10px 12px;background:#f8fafc;border-radius:10px;border:1px solid #f1f5f9;font-size:8px;line-height:1.5;color:#64748b;">
      <p><b>Conditions du devis.</b> Devis valable ${validiteDevisJours} jours à compter de sa date d'émission. Les prix sont exprimés en euros, hors taxes et toutes taxes comprises. Toute prestation non mentionnée sur ce devis fera l'objet d'une facturation complémentaire. ${devisEngageClient ? 'Le devis, une fois daté et signé avec la mention « bon pour accord », vaut commande.' : 'Ce devis est établi sans engagement : sa signature ne vous engage en rien, elle indique que vous validez le prix et souhaitez fixer un rendez-vous.'}</p>
      ${client.type === 'PROFESSIONNEL' ? `
      <p style="margin-top:4px;"><b>Paiement (client professionnel).</b> Paiement à réception de facture, sauf accord contraire. Tout retard de paiement entraîne de plein droit des pénalités de retard égales à trois fois le taux d'intérêt légal, ainsi qu'une indemnité forfaitaire de 40 € pour frais de recouvrement (articles L441-10 et D441-5 du Code de commerce). Aucun escompte n'est accordé pour paiement anticipé.</p>` : `
      <p style="margin-top:4px;"><b>Droit de rétractation (client particulier).</b> Pour tout contrat conclu à distance ou hors établissement, le client dispose d'un délai de 14 jours pour se rétracter, sans avoir à justifier de motif (articles L221-18 et suivants du Code de la consommation). Si le client demande que la prestation commence avant la fin de ce délai, il devra payer un montant proportionnel à ce qui a été fourni jusqu'à la communication de sa rétractation.</p>
      <p style="margin-top:4px;"><b>Médiation de la consommation.</b> En cas de litige, le client peut recourir gratuitement à un médiateur de la consommation, après une réclamation écrite auprès du prestataire (articles L611-1 et suivants du Code de la consommation).${settings.cgv_mediator ? ` <b>Médiateur : ${echapperHtmlDevis(String(settings.cgv_mediator))}.</b>` : ''}</p>`}
    </div>

    <!-- LEGAL FOOTER -->
    <div style="margin-top:16px;padding-top:16px;border-top:1px solid #f1f5f9;text-align:center;">
      <div style="opacity:0.8;">
        <p style="font-size:10px;color:#64748b;font-weight:900;text-transform:uppercase;letter-spacing:0.2em;margin-bottom:4px;">${companyName}</p>
        ${legalParts1.length > 0 ? `
        <div style="font-size:8px;color:#94a3b8;line-height:1.6;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;">
          ${legalParts1.join(' ')}
        </div>` : ''}
        ${legalParts2.length > 0 ? `
        <div style="font-size:8px;color:#94a3b8;line-height:1.6;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;">
          ${legalParts2.join(' ')}
        </div>` : ''}
      </div>
    </div>
  </div>

</div>
</body>
</html>`;
}

// La capture DOM→image (toPng) d'un devis annuel multi-passages (tableau très long) peut prendre un temps
// très long sans jamais rejeter ni progresser — contrairement à chaque étape qui l'entoure (iframe, images),
// elle n'a aucun repli. Sans ce timeout, un devis assez long bloque le bouton « Envoi... » indéfiniment, sans
// erreur visible (bug remonté par Emeni, jamais reproduit ni expliqué faute de cause identifiée à l'époque).
const PDF_CAPTURE_TIMEOUT_MS = 45000;
const withTimeout = <T,>(promise: Promise<T>, ms: number, message: string): Promise<T> =>
    Promise.race([
        promise,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
    ]);

export async function generateDevisPrestationPdf(params: Parameters<typeof buildDevisPrestationHtml>[0]): Promise<{ base64: string; blob: Blob }> {
    await chargerValiditeDevis();
    const htmlContent = buildDevisPrestationHtml(params);
    const { toPng } = await import('html-to-image');

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    // Assez haut pour ne jamais brider un devis annuel multi-passages (contenu réel mesuré ensuite via
    // scrollHeight, pas cette valeur) — un devis classique d'une page n'en est pas affecté.
    iframe.style.height = '2000mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-1';
    document.body.appendChild(iframe);

    await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        iframe.srcdoc = htmlContent;
        setTimeout(resolve, 2000);
    });

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
        const imgs = Array.from(iframeDoc.querySelectorAll('img'));
        if (imgs.length > 0) {
            await Promise.all(imgs.map((img) => new Promise<void>((res) => {
                if (img.complete) { res(); return; }
                img.onload = () => res();
                img.onerror = () => res();
                setTimeout(res, 1500);
            })));
        }
        // BUG TROUVÉ (2026-09-05, Emeni : « le logo ne s'affiche pas sur iPhone ») : le logo était dimensionné
        // en CSS via max-height/max-width + object-fit:contain — Safari iOS ne restitue pas cette combinaison
        // de façon fiable dans le <foreignObject> SVG utilisé par html-to-image pour sérialiser le DOM, le
        // logo disparaît alors silencieusement (aucune erreur, juste absent de l'image capturée), alors que
        // Chrome desktop l'affiche sans problème. Remplacé par une largeur/hauteur explicites en pixels,
        // calculées à partir des dimensions réelles de l'image une fois chargée (naturalWidth/naturalHeight,
        // fonctionne quel que soit le format — PNG, JPEG...), plus fiable sur tous les moteurs de rendu que
        // de laisser le navigateur appliquer object-fit lui-même.
        const logoImg = iframeDoc.querySelector<HTMLImageElement>('img[alt="Logo"]');
        if (logoImg && logoImg.naturalWidth && logoImg.naturalHeight) {
            const maxWidth = 250, maxHeight = 112;
            const ratio = logoImg.naturalWidth / logoImg.naturalHeight;
            let width = maxWidth, height = maxWidth / ratio;
            if (height > maxHeight) { height = maxHeight; width = maxHeight * ratio; }
            logoImg.style.width = `${Math.round(width)}px`;
            logoImg.style.height = `${Math.round(height)}px`;
            logoImg.style.maxWidth = '';
            logoImg.style.maxHeight = '';
            logoImg.style.objectFit = '';
        }
    }

    const targetEl = iframeDoc?.body?.firstElementChild as HTMLElement | null;
    if (!targetEl) {
        document.body.removeChild(iframe);
        throw new Error('Impossible de générer le devis.');
    }

    // Attend que les polices de l'iframe soient chargées AVANT de mesurer offsetTop/offsetHeight — sinon la
    // police de repli (plus large/étroite) est utilisée pour la mesure, décalant les zones interdites par
    // rapport à la position réelle dans l'image capturée par toPng() juste après.
    if (iframe.contentWindow && (iframe.contentWindow as Window & { document: Document }).document.fonts) {
        try { await (iframe.contentWindow as Window & { document: Document }).document.fonts.ready; } catch { /* ignore */ }
    }

    // Zones interdites à la découpe — demande d'Emeni (2026-09-03) : « on ne coupe aucun paragraphe, aucun
    // petit point, si la page ne permet pas de tout prendre ». Chaque ligne de prestation (<tr>), chaque
    // paragraphe (<p>) et chaque puce (<li>) est protégé individuellement (une table de devis annuel peut
    // dépasser largement une page, donc pas de sens à protéger le bloc entier — seulement à ne jamais couper
    // UNE unité en deux), plus le bloc récapitulatif financier (.pdf-avoid-break) en bas de page.
    const cssScale = 210 / targetEl.scrollWidth;
    const buildForbiddenEls = () => [
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('table')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('tr')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('p')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('li')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('.pdf-anchor-bottom')),
    ];
    let forbiddenEls = buildForbiddenEls();
    // Demande d'Emeni (2026-09-03) : une <table> entière est protégée en priorité (jamais coupée si elle
    // tient sur une page fraîche) — seul un tableau plus haut qu'une page entière retombe sur la protection
    // ligne par ligne (<tr>) déjà en place, pour ne jamais boucler indéfiniment.
    // BUG TROUVÉ (2026-09-03, Emeni : « la section 4 est coupée en deux ») : offsetTop est relatif à
    // offsetParent, pas à la page — pour un <tr>, offsetParent est souvent la <table> elle-même (voire une
    // cellule), pas targetEl. Les zones interdites des lignes de tableau étaient donc mesurées à une position
    // fausse, ce qui faussait la comparaison "top minimal" avec les blocs englobants (.pdf-avoid-break) et
    // laissait passer des coupures à l'intérieur de sections censées être protégées. getBoundingClientRect()
    // donne une position fiable quelle que soit l'imbrication (table, cellule, position...).
    let targetRectTop = targetEl.getBoundingClientRect().top;
    // BUG TROUVÉ (2026-09-04, Emeni : « le devis a plusieurs pages blanches ») : le <table> du devis annuel
    // dépasse très largement une page (des dizaines de lignes sur plusieurs passages) — le protéger comme un
    // bloc entier ne peut JAMAIS aboutir (il ne tiendra jamais sur une page fraîche), mais l'algorithme
    // essayait quand même de reculer jusqu'à son début à chaque page, produisant une page quasi vide juste
    // avant d'abandonner et de retomber sur la protection ligne par ligne. Un bloc plus haut qu'une page
    // entière est donc ignoré dès la mesure — seul un bloc qui PEUT réellement tenir sur une page fraîche
    // mérite d'être protégé en entier.
    const MAX_ATOMIC_BLOCK_HEIGHT_MM = 297 - 8 - 8; // pageHeight - marges haut/bas (constantes redéclarées plus bas, cf. pageBudget)
    const computeForbiddenRanges = () => forbiddenEls.map((el) => {
        const rect = el.getBoundingClientRect();
        return { top: (rect.top - targetRectTop) * cssScale, bottom: (rect.bottom - targetRectTop) * cssScale, tag: el.tagName.toLowerCase() };
    }).filter((r) => (r.bottom - r.top) <= MAX_ATOMIC_BLOCK_HEIGHT_MM);
    let forbiddenRanges = computeForbiddenRanges();
    // BUG TROUVÉ (2026-09-03, Emeni : « le petit 3 est coupé » sur le contrat — un titre de section restait
    // seul en bas de page, son contenu partant sur la page suivante). Rien ne protégeait un titre (<h2>,
    // <h3>) d'être séparé de ce qui le suit immédiatement. On étend sa zone protégée jusqu'au début de
    // l'élément suivant (+ une marge d'une ligne) pour forcer le titre entier à basculer avec le début de
    // son contenu si la page ne peut accueillir au moins un peu des deux ensemble.
    const headingKeepWithNext = Array.from(targetEl.querySelectorAll<HTMLElement>('h2, h3')).map((h) => {
        const rect = h.getBoundingClientRect();
        const top = (rect.top - targetRectTop) * cssScale;
        let bottom = (rect.bottom - targetRectTop) * cssScale;
        const next = h.nextElementSibling as HTMLElement | null;
        if (next) {
            const nextRect = next.getBoundingClientRect();
            bottom = Math.max(bottom, (nextRect.top - targetRectTop) * cssScale + 8);
        }
        return { top, bottom, tag: 'h' };
    });
    // Les deux dernières lignes du tableau restent avec le bloc des totaux : quand les totaux ne tiennent pas en bas de la page, ils ne
    // partent plus seuls sur une page vide, ils emmènent avec eux la fin du tableau (la ligne et sa réduction).
    const dernieresLignesAvecTotaux = (): { top: number; bottom: number; tag: string }[] => {
        const ancre = targetEl.querySelector<HTMLElement>('.pdf-anchor-bottom');
        const tables = targetEl.querySelectorAll<HTMLElement>('table');
        const lignes = tables.length ? Array.from(tables[tables.length - 1].querySelectorAll<HTMLElement>('tbody tr')) : [];
        if (!ancre || lignes.length < 3) return [];
        const debut = lignes[lignes.length - 2].getBoundingClientRect();
        const fin = ancre.getBoundingClientRect();
        const plage = { top: (debut.top - targetRectTop) * cssScale, bottom: (fin.bottom - targetRectTop) * cssScale, tag: 'keep' };
        return plage.bottom - plage.top <= MAX_ATOMIC_BLOCK_HEIGHT_MM ? [plage] : [];
    };
    forbiddenRanges.push(...headingKeepWithNext, ...dernieresLignesAvecTotaux());

    // Ancrage en bas de page (2026-09-05, Emeni : « la carte avec le total et les mentions légales doivent
    // toujours rester en bas de page », sur un devis d'une seule page comme sur le dernier bloc d'un devis
    // multi-pages). Le bloc .pdf-anchor-bottom (carte total + pied légal) est naturellement rendu juste après
    // le tableau, où qu'il tombe sur la page — simule ici la même logique de pagination que la découpe finale
    // pour savoir sur quelle page il atterrirait et quel budget il reste sur cette page, puis pousse le bloc
    // vers le bas via un simple margin-top pour qu'il colle exactement au bas de CETTE page (jamais forcé sur
    // la dernière page globale : un devis multi-pages garde son bloc en bas de la page où il tombe déjà).
    const anchorEl = targetEl.querySelector<HTMLElement>('.pdf-anchor-bottom');
    if (anchorEl) {
        const anchorRect = anchorEl.getBoundingClientRect();
        const anchorTop = (anchorRect.top - targetRectTop) * cssScale;
        const anchorHeight = (anchorRect.bottom - anchorRect.top) * cssScale;
        const rangesAboveAnchor = forbiddenRanges.filter((r) => r.top < anchorTop - 0.5);
        const SIM_PAGE_HEIGHT_MM = 297, SIM_CONTINUATION_TOP_MM = 8, SIM_BOTTOM_MM = 8;
        let simUsedHeight = 0, simFirstPage = true, pageStart = 0, pageBudgetAtLanding = 0;
        // Avance page par page (même règle de coupure que la boucle finale, y compris le renoncement à un
        // <table> qui gâcherait trop de place) jusqu'à trouver la page qui contient réellement anchorTop.
        while (true) {
            const pageBudgetSim = (simFirstPage ? SIM_PAGE_HEIGHT_MM : SIM_PAGE_HEIGHT_MM - SIM_CONTINUATION_TOP_MM) - SIM_BOTTOM_MM;
            const naiveBreakSim = simUsedHeight + pageBudgetSim;
            if (naiveBreakSim >= anchorTop) { pageStart = simUsedHeight; pageBudgetAtLanding = pageBudgetSim; break; }
            let breakAtSim = naiveBreakSim;
            for (const r of rangesAboveAnchor) {
                if (naiveBreakSim > r.top && naiveBreakSim < r.bottom && r.top > simUsedHeight + 1 && r.top < breakAtSim) {
                    if (r.tag === 'table' && (r.top - simUsedHeight) > 60) continue;
                    breakAtSim = r.top;
                }
            }
            if (breakAtSim <= simUsedHeight) breakAtSim = simUsedHeight + pageBudgetSim;
            simUsedHeight = breakAtSim;
            simFirstPage = false;
        }
        const pageBottomLimit = pageStart + pageBudgetAtLanding;
        const spacerMm = pageBottomLimit - anchorTop - anchorHeight;
        // Le bloc ne descend en bas de page que si cette page porte déjà une bonne part du devis. Quand les totaux atterrissent seuls sur
        // une page presque vide (le tableau finissait en bas de la page précédente), les coller au bas laissait un grand vide au-dessus,
        // et un dépassement de quelques millimètres créait en plus une page blanche : ils restent alors juste sous le haut de la page.
        const contenuAvantBloc = anchorTop - pageStart;
        const SECURITE_BAS_MM = 3;
        if (spacerMm - SECURITE_BAS_MM > 0.5 && contenuAvantBloc > pageBudgetAtLanding * 0.4) {
            anchorEl.style.marginTop = `${(spacerMm - SECURITE_BAS_MM) / cssScale}px`;
            // Le padding du conteneur (.devis-wrap) laisse déjà un blanc de 8mm en bas — le spacer vise le
            // même bas de page que la coupure finale, jamais au-delà de scrollHeight.
            targetRectTop = targetEl.getBoundingClientRect().top;
            forbiddenEls = buildForbiddenEls();
            forbiddenRanges = computeForbiddenRanges();
            forbiddenRanges.push(...headingKeepWithNext, ...dernieresLignesAvecTotaux());
        }
    }
    if ((window as any).__PDF_DEBUG__) console.log('[pdf-debug-devis] ranges', forbiddenRanges.map(r => ({ top: Math.round(r.top * 10) / 10, bottom: Math.round(r.bottom * 10) / 10 })));

    try {
        // Ratio de capture plafonné pour rester sous la limite de taille de canvas des navigateurs mobiles
        // (Safari iOS notamment : un canvas trop grand — long devis multi-page à pixelRatio fixe 1.5 —
        // produit une image vide/tronquée, sans erreur visible : bug remonté par Emeni, "PDF vide/cassé sur
        // téléphone" alors qu'il s'affichait bien sur ordinateur). Même correctif que contractPdfGenerator.ts.
        const safePixelRatio = Math.min(1.5, 4000 / Math.max(targetEl.scrollWidth, targetEl.scrollHeight, 1));
        const imgData = await withTimeout(
            toPng(targetEl, { cacheBust: true, pixelRatio: safePixelRatio, backgroundColor: '#ffffff', width: targetEl.scrollWidth, height: targetEl.scrollHeight }),
            PDF_CAPTURE_TIMEOUT_MS,
            'La génération du PDF a pris trop de temps (devis très long ?). Réessayez, ou raccourcissez le devis si le problème persiste.',
        );
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = 210, pageHeight = 297;
        const imageProps = pdf.getImageProperties(imgData);
        // Multi-page comme le contrat d'abonnement : un devis annuel par passages dépasse largement une
        // page. Mise à l'échelle sur la LARGEUR seulement (jamais sur la hauteur, qui compresserait tout
        // sur une seule page), puis découpage vertical page par page — en reculant la coupure jusqu'au début
        // de la ligne/du bloc en cours si la coupure naïve à 297mm tombait dedans.
        const scale = pageWidth / imageProps.width;
        const imageWidth = imageProps.width * scale;
        const imageHeight = imageProps.height * scale;

        // BUG TROUVÉ (2026-09-03, Emeni : contenu qui semblait dupliqué d'une page à l'autre) : jsPDF
        // addImage() peint TOUJOURS un plein rectangle pageHeight quelle que soit la hauteur "voulue" —
        // reculer `breakAt` pour protéger un bloc ne l'empêchait donc pas d'apparaître quand même en bas de
        // la page courante (l'image complète était repeinte à chaque fois, seul le point de départ de la
        // page SUIVANTE changeait) : le contenu entre le point protégé et le bas de page se retrouvait
        // affiché deux fois. Chaque page utilise désormais une image RECADRÉE à sa tranche exacte (via un
        // canvas), jamais l'image complète repositionnée.
        const sourceImg = new Image();
        await new Promise<void>((resolve, reject) => { sourceImg.onload = () => resolve(); sourceImg.onerror = () => reject(new Error('Image de rendu invalide.')); sourceImg.src = imgData; });
        const pxPerMm = imageProps.width / imageWidth;

        // BUG TROUVÉ (2026-09-03, Emeni : « la marge en haut de page est trop étroite sur les pages 2 et 3 »)
        // : seule la première page a une marge en haut (incluse dans le contenu capturé, padding:8mm du
        // conteneur) — les pages suivantes démarrent une tranche recadrée en plein milieu du document, sans
        // aucune marge. On réserve donc un blanc de 8mm en haut de chaque page à partir de la 2e, et un blanc
        // de 8mm en bas de CHAQUE page (y compris la 1re si elle n'est pas la dernière).
        const CONTINUATION_TOP_MARGIN_MM = 8;
        const BOTTOM_MARGIN_MM = 8;
        let usedHeight = 0;
        let firstPage = true;
        while (usedHeight < imageHeight - 0.5) {
            const pageBudget = (firstPage ? pageHeight : pageHeight - CONTINUATION_TOP_MARGIN_MM) - BOTTOM_MARGIN_MM;
            const naiveBreak = Math.min(usedHeight + pageBudget, imageHeight);
            // Retient la collision la plus proche (top minimal), jamais la première trouvée dans l'ordre du DOM.
            let breakAt = naiveBreak;
            for (const r of forbiddenRanges) {
                if (naiveBreak > r.top && naiveBreak < r.bottom && r.top > usedHeight + 1 && r.top < breakAt) {
                    // BUG TROUVÉ (2026-09-05, Emeni : « le devis a une page 1 quasi vide ») : protéger le
                    // <table> entier (cf. commentaire plus haut) le renvoyait en bloc à la page suivante dès
                    // qu'il touchait la coupure naïve, quelle que soit la place déjà gâchée sur la page en
                    // cours (ex: un en-tête + destinataire volumineux laissant peu de place, un tableau
                    // pourtant court renvoyé en entier page 2, page 1 aux 2/3 blanche). Un <table> n'est donc
                    // reporté en bloc que si ça ne gâche pas plus de MAX_WASTED_SPACE_MM — au-delà, on renonce
                    // à la protection du tableau entier et on laisse la boucle retomber sur la protection
                    // ligne par ligne (<tr>, toujours dans forbiddenRanges), qui remplit la page en cours au
                    // maximum sans jamais couper une ligne en deux.
                    const MAX_WASTED_SPACE_MM = 60;
                    if (r.tag === 'table' && (r.top - usedHeight) > MAX_WASTED_SPACE_MM) continue;
                    breakAt = r.top;
                }
            }
            // BUG TROUVÉ (2026-09-03, Emeni : un filet de texte flou reste visible au bas de la page qui
            // précède un bloc protégé) : couper l'image PILE au pixel où commence le bloc protégé laisse
            // parfois échapper l'antialiasing du haut d'une lettre. Petite marge de sécurité pour finir
            // chaque page sur du blanc franc avant le bloc protégé suivant.
            // BUG TROUVÉ (2026-09-04, Emeni : « le devis a plusieurs pages blanches ») : quand plusieurs
            // éléments protégés sont très rapprochés (ex. lignes d'en-tête de passage, très fines), reculer de
            // 4mm PUIS forcer un plancher à usedHeight+1 ne faisait avancer que d'1mm par page — plusieurs
            // pages quasi vides de suite avant d'atteindre un contenu assez grand pour s'en échapper. On
            // n'applique la marge que si elle laisse un progrès réel ; sinon on renonce à la marge plutôt qu'au
            // progrès (léger risque de filet visuel dans ce cas rare, largement préférable à des pages vides).
            if (breakAt < naiveBreak) {
                const withMargin = breakAt - 4;
                if (withMargin > usedHeight + 1) breakAt = withMargin;
            }
            if (breakAt <= usedHeight) breakAt = usedHeight + pageBudget; // ligne/bloc plus haut qu'une page : coupe quand même, jamais de boucle infinie
            if ((window as any).__PDF_DEBUG__) console.log('[pdf-debug-devis] break', { usedHeight, naiveBreak, breakAt, imageHeight });

            const sliceHeightMm = breakAt - usedHeight;
            const srcY = Math.round(usedHeight * pxPerMm);
            const srcH = Math.max(1, Math.round(sliceHeightMm * pxPerMm));
            const canvas = document.createElement('canvas');
            canvas.width = imageProps.width;
            canvas.height = srcH;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(sourceImg, 0, srcY, imageProps.width, srcH, 0, 0, imageProps.width, srcH);
            const sliceData = canvas.toDataURL('image/png');

            if (!firstPage) pdf.addPage();
            pdf.addImage(sliceData, 'PNG', 0, firstPage ? 0 : CONTINUATION_TOP_MARGIN_MM, imageWidth, sliceHeightMm, undefined, 'SLOW');
            usedHeight = breakAt;
            firstPage = false;
        }

        const blob = pdf.output('blob');
        const base64: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
            reader.onerror = () => reject(new Error('Lecture du PDF impossible.'));
            reader.readAsDataURL(blob);
        });
        return { base64, blob };
    } finally {
        document.body.removeChild(iframe);
    }
}
